import * as yup from 'yup';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SportType } from '../../__generated__/graphql';
import { assetUrl, getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import AddEventMutation from './AddEventMutation';
import DateRangeForm, { dateRangeObject } from '../Common/DateRangeForm';
import UI from '../../common/UI';
import WebsiteInput from '../Common/WebsiteInput';
import useGroupedSportTypes from './useGroupedSportTypes';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

const addEventSchema = yup.object().shape({
  title: yup.string().required(),
  sport_type: yup.string().required(),
  location: yup.object().required().shape({
    id: yup.string().required(),
  }),
  ...dateRangeObject(true, false),
});

const AddEventPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: {
      title: '',
      website_url: null,
      sport_type: '' as SportType,
      location: {
        id: null,
        title: null,
        description: null,
        address: null,
        country: null,
        coordinates: [],
      },
      start_date: null,
      end_date: null,
    },
  });

  const [addEvent, { error, loading }] = useMutation(AddEventMutation, {
    variables: {
      input: {
        info: {
          title: form.values.title,
          start_date: form.values.start_date,
          end_date: form.values.end_date || form.values.start_date,
          location: form.values.location,
          website_url: form.values.website_url,
        },
        sport_type: form.values.sport_type,
        project: {
          id: project.id,
        },
      },
    },
    onCompleted: ({ addEvent: event }) => {
      notifier.success(t('notifications.event_added'));

      history.push(`/${project.id}/events/${event.id}`);
    },
    onError: () => {
      scrollTo('AddEventForm');
    },
  });

  const { valid, errors } = useValidation({
    schema: addEventSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { popularSportTypes, otherSportTypes } = useGroupedSportTypes();

  const locationBlurTimerRef = useRef<number>();

  // Clear timer on unmount.
  useEffect(() => () => window.clearTimeout(locationBlurTimerRef.current), []);

  const handleLocationBlur = () => {
    window.clearTimeout(locationBlurTimerRef.current);

    // Save the touched event after a small delay, to prevent flashing of validation errors.
    locationBlurTimerRef.current = window.setTimeout(() => {
      form.setTouched('location', true);
    }, 300);
  };

  return (
    <UI.Section sc={{ compact: true }} id="AddEventForm">
      <UI.GridContainer>
        <UI.Div sc={{ textAlign: 'center' }}>
          <img src={assetUrl('images/event.svg')} alt="" height={120} />
          <UI.H1 sc={{ my: 1 }}>
            {t('lets_create_event')}
          </UI.H1>
          <UI.Paragraph sc={{ big: true, mb: 0 }} style={{ textWrap: 'balance' }}>
            {t('create_an_event_description')}
          </UI.Paragraph>
        </UI.Div>
        <UI.Form onSubmit={() => addEvent()}>
          <UI.Card sc={{ spacing: 'lg' }}>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
                <UI.InputLabel htmlFor="title">
                  {t('event_title')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.DebouncedInput
                  id="title"
                  {...form.text('title')}
                  sc={{ size: 'lg' }}
                  placeholder={t('event_title_placeholder')}
                />
                <UI.ErrorMessages attribute={t('event_title')} errors={errors.title} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.sport_type, touched: form.touched.sport_type }}>
                <UI.InputLabel htmlFor="sport_type">
                  {t('main_sport_type')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('multiple_sports_label')}
                </UI.InputDescription>
                <UI.Select id="sport_type" {...form.select('sport_type')}>
                  {!form.values.sport_type && <option value=""> </option>}
                  <optgroup label={t('popular')}>
                    {popularSportTypes.map((category: string, index: number) => (
                      <option {...form.option(category)} key={index}>
                        {category && t(`common:sport_types.${category}`)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('other')}>
                    {otherSportTypes.map((category: string, index: number) => (
                      <option {...form.option(category)} key={index}>
                        {category && t(`common:sport_types.${category}`)}
                      </option>
                    ))}
                  </optgroup>
                </UI.Select>
                <UI.ErrorMessages attribute={t('sport_type')} errors={errors.sport_type} />
              </UI.InputGroup>

              <DateRangeForm
                form={form}
                errors={errors}
                requireStart
                allowFullDay
                labels={{
                  startDate: t('date'),
                  startTime: t('time'),
                }}
              />

              <UI.InputGroup sc={{ valid: !errors['location.id'], touched: form.touched.location }}>
                <UI.InputLabel htmlFor="location">
                  {t('location')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.GoogleLocationInput
                  id="location"
                  value={form.values.location}
                  onChange={(location) => form.set('location', location)}
                  onBlur={() => handleLocationBlur()}
                  placeholder={t('location_placeholder')}
                />
                <UI.ErrorMessages attribute={t('location')} errors={errors['location.id']} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.website_url, touched: form.touched.website_url }}>
                <UI.InputLabel htmlFor="website_url">
                  {t('website')}
                </UI.InputLabel>
                <WebsiteInput form={form} formKey="website_url" id="website_url" />
                <UI.ErrorMessages attribute={t('website')} errors={errors.website_url} />
              </UI.InputGroup>

              <UI.HR />

              <UI.Div sc={{ textAlign: ['center', 'right'] }}>
                <UI.FormButtons
                  form={form}
                  submitLabel={t('create_event')}
                  valid={valid}
                  loading={loading}
                />
              </UI.Div>
            </UI.FormGrid>
          </UI.Card>
        </UI.Form>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default AddEventPage;
